import { useEffect, useRef, useState } from "react";
import { useBlocker, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { CustomerClass, CustomerDetailDto } from "@hisense/shared";
import { Lock, Plus } from "lucide-react";
import { PageBody, PageHeader } from "@/components/ui/PageHeader";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { CustomerContactsEditor, MAX_CONTACTS, blankContact, contactsToInput, isBlank, type ContactForm } from "@/components/customers/CustomerContactsEditor";
import { contactSideColumnsUsed, customerFormFrom, type CustomerFormDraft } from "@/components/customers/customerFormLayout";
import { FieldGrid, FormGroup, Hint, SPAN_CLASS } from "@/components/ui/FormLayout";
import { useCreateCustomer, useCustomer, useCustomerRequirements, useSettings, useUpdateCustomer } from "@/api/hooks";
import { ApiClientError, apiFetch, getLocalizedError } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { CustomerRequirementsEditor, requirementsFromDto, requirementsToInput, type RequirementDraft } from "@/components/customers/CustomerRequirementsEditor";
import { useCanSeePrivate, useDataRegion, useFollowedOnly, useMenuAccess } from "@/app/access";
import { useAuth } from "@/app/auth";
import { FollowersPicker } from "@/components/customers/FollowersPicker";

const FORM_ID = "customer-editor-form";

export function CustomerEditorPage() {
  const { id } = useParams();
  return <CustomerEditorLoader key={id ?? "new"} id={id} />;
}

function CustomerEditorLoader({ id }: { id?: string }) {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { data, isLoading, error, refetch } = useCustomer(id);
  usePageCrumbs(!id
    ? [{ label: t("customers.newCustomer") }]
    : data
      ? [{ label: data.companyName, to: `/customers/${id}${search}` }, { label: t("common.edit") }]
      : [{ label: t(error ? "customers.errorLoad" : "common.loading") }], `/customers${search}`);
  if (!id) return <CustomerEditorForm />;
  if (isLoading) return <PageBody><LoadingState label={t("customers.loading")} /></PageBody>;
  if (error || !data) return <PageBody><ErrorState message={t("customers.errorLoad")} onRetry={() => void refetch()} /></PageBody>;
  return <CustomerEditorForm key={id} existing={data} />;
}

/** Initialize after the record loads; background refetches never overwrite a draft. */
function CustomerEditorForm({ existing }: { existing?: CustomerDetailDto }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const isNew = !existing;
  const listTo = `/customers${search}`;
  const detailTo = existing ? `/customers/${existing.id}${search}` : listTo;
  const returnToList = typeof state === "object" && state !== null && "returnToList" in state && state.returnToList === true;
  const cancelTo = returnToList ? listTo : detailTo;
  const { data: settings } = useSettings();
  const { user } = useAuth();
  const create = useCreateCustomer();
  const update = useUpdateCustomer(existing?.id ?? "new");
  // Tài khoản chỉ một khu vực: khách mới mặc định thuộc khu vực đó và không đổi được phân loại (máy chủ cũng chặn).
  const region = useDataRegion();
  // Tài khoản "chỉ khách mình theo đơn": khách mới có sẵn tên mình, và tên mình không bỏ được (máy chủ cũng giữ lại).
  const followedOnly = useFollowedOnly();
  const selfFollower = followedOnly ? user?.id ?? null : null;
  // Khách riêng tư / liên hệ riêng tư (30/09/2026): chỉ quản trị viên thấy ô và gửi cờ (máy chủ từ chối người khác).
  const canMarkPrivate = useCanSeePrivate();
  const [f, setF] = useState<CustomerFormDraft>(() => customerFormFrom(existing, region, selfFollower));
  /** "Hiện cột phụ" của bảng người liên hệ — có người dùng cột phụ thì cột phụ luôn hiện (contactSideColumnsUsed). */
  const [sideOpen, setSideOpen] = useState(false);
  const [contacts, setContacts] = useState<ContactForm[]>(() => existing?.contacts.length
    ? existing.contacts.map((c) => ({
      name: c.name, phone1: c.phone1 ?? "", phone2: c.phone2 ?? "", email: c.email ?? "",
      wechat: c.wechat ?? "", section: c.section ?? "", brandName: c.brandName ?? "", team: c.team ?? "",
      isPrivate: c.isPrivate,
    })) : [blankContact()]);
  // Vừa tạo khách mà lưu yêu cầu lỗi: trang tạo chuyển sang trang sửa, mang theo các mục chưa lưu và câu báo lỗi.
  const carried = (state as { pendingRequirements?: RequirementDraft[] } | null)?.pendingRequirements;
  const [error, setError] = useState<string | null>(() => (carried?.length ? t("evidence.reqSaveFailed") : null));
  const [codeError, setCodeError] = useState<string | null>(null);
  // Yêu cầu sản phẩm / bao bì (v1.8 mục 2.3): nạp một lần, ghi riêng SAU khi lưu khách thành công.
  const queryClient = useQueryClient();
  // Điều khoản thanh toán + yêu cầu sản phẩm / bao bì: ô tick riêng "Điều khoản & yêu cầu đặt hàng" (mục 8, 04/11/2026). Máy chủ kiểm.
  const termsMenu = useMenuAccess("customerTerms");
  const seesTerms = termsMenu("view");
  const canSetTerms = isNew ? termsMenu("create") || termsMenu("update") : termsMenu("update");
  const savedRequirements = useCustomerRequirements(existing?.id, seesTerms);
  const [requirements, setRequirements] = useState<RequirementDraft[]>(() => carried ?? []);
  const [requirementsDirty, setRequirementsDirty] = useState(Boolean(carried?.length));
  useEffect(() => {
    if (savedRequirements.data && !requirementsDirty) setRequirements(requirementsFromDto(savedRequirements.data));
  }, [savedRequirements.data, requirementsDirty]);
  // Lưu là thay cả danh sách: chưa tải xong / tải lỗi mà cho sửa thì lưu sẽ xoá các mục cũ. Khách mới thì không có gì để tải.
  const requirementsReady = isNew || !seesTerms || savedRequirements.isSuccess;
  // PUT yêu cầu đi theo ô Sửa của menu Khách hàng (kể cả lúc vừa tạo).
  const customersMenu = useMenuAccess("customers");
  const canEditRequirements = customersMenu("update") && termsMenu("update");
  const [uploading, setUploading] = useState(0);
  const [savingRequirements, setSavingRequirements] = useState(false);
  const busy = create.isPending || update.isPending || savingRequirements;
  const snapshot = JSON.stringify({ f, contacts });
  const initialSnapshot = useRef(snapshot);
  const allowNavigation = useRef(false);
  const dirty = snapshot !== initialSnapshot.current || requirementsDirty;
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    !allowNavigation.current && (dirty || busy) &&
    (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));
  useEffect(() => {
    if (!dirty && !busy) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (allowNavigation.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty, busy]);
  const patch = (p: Partial<typeof f>) => setF((prev) => ({ ...prev, ...p }));
  const patchContacts = (next: ContactForm[]) => setContacts(next);
  const paymentTerms = settings?.businessOperations.optionLists.paymentTerms ?? [];
  const legacyTerm = !!f.paymentTerm && !paymentTerms.includes(f.paymentTerm);
  const filledContacts = contacts.filter((c) => !isBlank(c)).length;
  const sideUsed = contactSideColumnsUsed(contacts);
  const showSide = sideOpen || sideUsed;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (uploading > 0) {
      setError(t("evidence.waitUpload"));
      return;
    }
    setError(null);
    setCodeError(null);
    const conv = contactsToInput(contacts);
    if (!Array.isArray(conv)) {
      setError(t("customers.errorContactName", { n: conv.error.row }));
      return;
    }
    const body = {
      companyName: f.companyName.trim(),
      code: f.code.trim() || null,
      // Empty strings explicitly clear previous values; undefined would omit the update.
      // Không có quyền ghi điều khoản thì không gửi ô đó (để nguyên giá trị đang lưu).
      address: f.address.trim(), phone: f.phone.trim(), ...(canSetTerms ? { paymentTerm: f.paymentTerm } : {}),
      classification: f.classification, pProcessDefault: f.pProcessDefault,
      followerUserIds: f.followerUserIds, contacts: conv,
      ...(canMarkPrivate ? { isPrivate: f.isPrivate } : {}),
    };
    try {
      const result = isNew ? await create.mutateAsync(body) : await update.mutateAsync(body);
      if (requirementsDirty) {
        setSavingRequirements(true);
        try {
          await apiFetch(`/customers/${result.data.id}/requirements`, { method: "PUT", body: requirementsToInput(requirements) });
          setRequirementsDirty(false);
          await queryClient.invalidateQueries({ queryKey: ["customer-requirements", result.data.id] });
        } catch {
          // Khách đã lưu: không để người dùng tưởng mất cả hồ sơ — báo rõ phần chưa lưu và mở lại đúng khách vừa lưu.
          setError(t("evidence.reqSaveFailed"));
          if (isNew) {
            // Trang sửa là lần mount mới: mang các mục chưa lưu sang (ảnh xem trước đã thu hồi cùng trang này) để báo lỗi và lưu lại.
            allowNavigation.current = true;
            navigate(`/customers/${result.data.id}/edit${search}`, {
              replace: true,
              state: { pendingRequirements: requirements.map((r) => ({ ...r, photos: r.photos.map((p) => ({ ...p, localUrl: undefined })) })) },
            });
          }
          return;
        } finally {
          setSavingRequirements(false);
        }
      }
      allowNavigation.current = true;
      navigate(`/customers/${result.data.id}${search}`, { replace: true, state: { customerSaved: true } });
    } catch (err) {
      // Mã khách trùng: báo ngay dưới ô Mã khách (máy chủ kiểm trên MỌI khách, kể cả khách mình không thấy được).
      if (err instanceof ApiClientError && err.code === "customer_code_taken") setCodeError(t("customers.codeTaken"));
      setError(getLocalizedError(err, t, "customers.errorSave"));
    }
  };

  return (
    <PageBody className="space-y-4">
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title={t("common.unsavedTitle")}
        description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")}
        cancelLabel={t("common.keepEditing")}
        tone="warn" busy={busy}
        onCancel={() => { if (blocker.state === "blocked") blocker.reset(); }}
        onConfirm={() => { if (blocker.state === "blocked") blocker.proceed(); }}
      />
      <PageHeader title={t(isNew ? "customers.newCustomer" : "customers.editCustomer")} subtitle={existing?.companyName} />
      {/* Native validation stays ON: `required` on the company name and the
          contact `type="email"` inputs catch what the API would only reject
          after a round-trip that costs the whole 15-row form. */}
      <form id={FORM_ID} onSubmit={submit} className="space-y-4">
        <fieldset disabled={busy} className="min-w-0 space-y-4">
          {/* Bố cục (19/09/2026, cùng chuẩn các trang sửa khác — customerFormLayout.ts): mỗi hàng kín 12 ô, gợi ý nằm dưới ô. */}
          <FormGroup id="customer-group-company" title={t("customers.sectionCompany")} hint={t("customers.companyHint")}>
            <FieldGrid>
              <div className={SPAN_CLASS[6]}>
                <FieldLabel htmlFor="c-name">{t("customers.company")}</FieldLabel>
                <Input
                  id="c-name"
                  required
                  autoFocus
                  minLength={2}
                  maxLength={200}
                  disabled={busy}
                  value={f.companyName}
                  onChange={(e) => patch({ companyName: e.target.value })}
                />
                <Hint>{t("customers.required")}</Hint>
              </div>
              <div className={SPAN_CLASS[3]}>
                <FieldLabel htmlFor="c-code">{t("customers.code")}</FieldLabel>
                <Input id="c-code" className="font-mono" maxLength={30} disabled={busy} value={f.code} aria-invalid={codeError ? true : undefined} aria-describedby={codeError ? "c-code-error" : undefined} onChange={(e) => { setCodeError(null); patch({ code: e.target.value }); }} />
                {codeError ? <p id="c-code-error" role="alert" className="mt-1 text-xs text-danger">{codeError}</p> : <Hint>{t("customers.codeHint")}</Hint>}
              </div>
              <div className={SPAN_CLASS[3]}>
                <FieldLabel htmlFor="c-phone">{t("common.phone")}</FieldLabel>
                <Input id="c-phone" type="tel" className="font-mono" maxLength={60} disabled={busy} value={f.phone} onChange={(e) => patch({ phone: e.target.value })} />
              </div>
              <div className={SPAN_CLASS[12]}>
                <FieldLabel htmlFor="c-addr">{t("common.address")}</FieldLabel>
                <Input id="c-addr" maxLength={500} disabled={busy} value={f.address} onChange={(e) => patch({ address: e.target.value })} />
              </div>
            </FieldGrid>
          </FormGroup>

          <FormGroup id="customer-group-sales" title={t("customers.sectionSales")} hint={t("customers.salesHint")}>
            <FieldGrid>
              {seesTerms && <div className={SPAN_CLASS[4]}>
                <FieldLabel htmlFor="c-term">{t("customers.terms")}</FieldLabel>
                <Select id="c-term" containerClassName="w-full" disabled={busy || !canSetTerms} value={f.paymentTerm} onChange={(e) => patch({ paymentTerm: e.target.value })}>
                  <option value="">{t("customers.selectTerm")}</option>
                  {legacyTerm ? <option value={f.paymentTerm}>{f.paymentTerm}</option> : null}
                  {paymentTerms.map((term) => (
                    <option key={term} value={term}>{term}</option>
                  ))}
                </Select>
              </div>}
              <div className={SPAN_CLASS[4]}>
                <FieldLabel htmlFor="c-class">{t("customers.classification")}</FieldLabel>
                <Select id="c-class" containerClassName="w-full" disabled={busy || !!region} value={f.classification} onChange={(e) => patch({ classification: e.target.value as CustomerClass })}>
                  <option value="vietnam">{t("customers.vietnam")}</option>
                  <option value="china">{t("customers.china")}</option>
                </Select>
                {region ? <Hint>{t("customers.classificationLocked")}</Hint> : null}
              </div>
              <div className={SPAN_CLASS[4]}>
                <FieldLabel htmlFor="c-cs">{t("customers.cs")}</FieldLabel>
                <FollowersPicker
                  id="c-cs"
                  value={f.followerUserIds}
                  onChange={(followerUserIds) => patch({ followerUserIds })}
                  region={f.classification}
                  known={existing?.followers ?? []}
                  lockedId={selfFollower}
                  disabled={busy}
                  // Người theo đơn không phải quản trị viên không thấy khách riêng tư — nói ngay tại ô, không để họ tưởng vẫn theo đơn được.
                  warning={canMarkPrivate && f.isPrivate && f.followerUserIds.length ? t("customers.followersPrivate") : null}
                />
              </div>
              <div className={SPAN_CLASS[12]}>
                <label htmlFor="c-pprocess" className="flex items-center gap-2 text-sm font-medium text-ink">
                  <input
                    id="c-pprocess"
                    type="checkbox"
                    disabled={busy}
                    checked={f.pProcessDefault}
                    onChange={(e) => patch({ pProcessDefault: e.target.checked })}
                    className="size-3.5 accent-[color:var(--color-brand)]"
                  />
                  {t("lot.pProcessDefault")}
                </label>
                <Hint>{t("lot.pProcessDefaultHint")}</Hint>
              </div>
              {canMarkPrivate ? (
                <div className={SPAN_CLASS[12]}>
                  <label htmlFor="c-private" className="flex items-center gap-2 text-sm font-medium text-ink">
                    <input
                      id="c-private"
                      type="checkbox"
                      disabled={busy}
                      checked={f.isPrivate}
                      onChange={(e) => patch({ isPrivate: e.target.checked })}
                      className="size-3.5 accent-[color:var(--color-brand)]"
                    />
                    <Lock className="size-3.5 text-ink-soft" aria-hidden="true" />
                    {t("customers.private")}
                  </label>
                  <Hint>{t("customers.privateHint")}</Hint>
                </div>
              ) : null}
            </FieldGrid>
          </FormGroup>

          {/* Người liên hệ — một bảng: bốn cột chính, cột phụ ẩn khi không ai dùng. */}
          <FormGroup
            id="customer-group-contacts"
            flush
            title={t("customers.sectionContacts")}
            hint={t("customers.contactsCountHint", { count: filledContacts, max: MAX_CONTACTS })}
            actions={
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={sideUsed}
                  title={sideUsed ? t("customers.sideLocked") : t("customers.sideColumns")}
                  aria-pressed={showSide}
                  onClick={() => setSideOpen((open) => !open)}
                >
                  {showSide ? t("customers.sideHide") : t("customers.sideShow")}
                </Button>
                <Button type="button" variant="secondary" size="sm" disabled={busy || contacts.length >= MAX_CONTACTS}
                  onClick={() => patchContacts([...contacts, blankContact()])}>
                  <Plus className="size-4" /> {t("customers.addContact")}
                </Button>
              </>
            }
          >
            {canMarkPrivate ? (
              <p className="flex items-start gap-1.5 border-b border-line px-4 py-2 text-xs text-ink-soft">
                <Lock className="mt-px size-3.5 shrink-0" aria-hidden="true" />
                <span>{t("customers.contactsPrivateHint")}</span>
              </p>
            ) : null}
            <CustomerContactsEditor contacts={contacts} onChange={patchContacts} disabled={busy} showSide={showSide} privacy={canMarkPrivate} />
          </FormGroup>

          {/* Yêu cầu sản phẩm và bao bì của khách (v1.8 mục 2.3) — cuối hồ sơ khách như khách yêu cầu. */}
{seesTerms && (
          <FormGroup id="customer-group-requirements" title={t("evidence.reqTitle")} hint={t("evidence.reqHint")}>
            {!requirementsReady ? (
              savedRequirements.isError ? (
                <div className="flex flex-wrap items-center gap-2 text-xs text-danger" role="alert">
                  {t("evidence.reqLoadFailed")}
                  <Button type="button" size="sm" variant="secondary" onClick={() => void savedRequirements.refetch()}>
                    {t("common.retry")}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-ink-faint">{t("evidence.reqLoading")}</p>
              )
            ) : (
              <>
                {!canEditRequirements ? <p className="mb-1 text-xs text-ink-faint">{t("evidence.reqNeedEditRight")}</p> : null}
                <CustomerRequirementsEditor
                  value={requirements}
                  readOnly={!canEditRequirements}
                  onBusyChange={setUploading}
                  onChange={(next) => {
                    setRequirementsDirty(true);
                    setRequirements(next);
                  }}
                />
              </>
            )}
          </FormGroup>
          )}
        </fieldset>
        {error && <p role="alert" className="rounded-sm bg-danger-tint px-3 py-2 text-sm text-danger">{error}</p>}
        {/* Một thanh Hủy / Lưu dính đáy, cùng kiểu các trang sửa khác. */}
        <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-md border border-line bg-surface px-4 py-3 shadow-[var(--shadow-card)]">
          <p className="mr-auto text-xs text-ink-faint">{t("customers.saveHint")}</p>
          <Button type="button" variant="secondary" disabled={busy} onClick={() => navigate(cancelTo)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={busy || uploading > 0 || f.companyName.trim().length < 2} title={uploading > 0 ? t("evidence.waitUpload") : undefined}>
            {busy ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </form>

    </PageBody>
  );
}
